/**
 * UIManager - Handles all UI interactions, panels, and button states
 * Coordinates between map interactions and UI elements
 */
class UIManager {
    static CONFIG = {
        // Panel IDs
        PROJECTS_PANEL_ID: 'projects-panel',
        FILTERS_PANEL_ID: 'filters-panel',
        // Toggle button IDs
        TOGGLE_PROJECTS_BTN_ID: 'toggle-projects-panel',
        TOGGLE_FILTERS_BTN_ID: 'toggle-filters-panel',
        TOGGLE_FILTERS_BTN_ALT_ID: 'toggle-filters-panel-alt', // Mobile version
        // Close button IDs
        CLOSE_PROJECTS_BTN_ID: 'close-projects-panel',
        CLOSE_FILTERS_BTN_ID: 'close-filters-panel',
        // Scroll button IDs
        SCROLL_DOWN_BTN_ID: 'scroll-to--down',
        SCROLL_UP_BTN_ID: 'scroll-to--up',
        // Map control button IDs
        MAP_ZOOM_IN_BTN_ID: 'map-zoom-in',
        MAP_ZOOM_OUT_BTN_ID: 'map-zoom-out',
        MAP_ORIENTATION_BTN_ID: 'map-orientation',
        RESET_MAP_VIEW_BTN_ID: 'resetMapView', // Fly out / expand map view
        // Content container
        CONTENT_WRAPPER_CLASS: 'content-wrapper',
        // Panel dimensions (in pixels)
        PROJECTS_PANEL_WIDTH: 500, 
        // CSS Classes
        ACTIVE_CLASS: 'is--active',
        HIDDEN_CLASS: 'is--hidden',
    };
    constructor(mapManager, markerManager, dataManager) {
        if (!mapManager || !markerManager || !dataManager) {
            throw new Error('UIManager requires mapManager, markerManager, and dataManager');
        }
        this.mapManager = mapManager;
        this.markerManager = markerManager;
        this.dataManager = dataManager;
        this.activeProjectSlug = null;
        this.isPanelInitialized = false;
        this.filterManager = null; // Will be set after FilterManager is created
        // Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Automatically updates this.isMobile when window is resized
     * @private
     */
    _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e) => {
            this.isMobile = e.matches;
        });
    }
    /**
     * Set FilterManager reference and register callbacks
     * Called after FilterManager is instantiated
     * @param {Object} filterManager - FilterManager instance
     */
    setFilterManager(filterManager) {
        this.filterManager = filterManager;
        // Register callback for filter changes to update project item visibility
        if (filterManager && typeof filterManager.onFilterChange === 'function') {
            filterManager.onFilterChange((filteredLocations) => {
                this.updateProjectItemsVisibility(filteredLocations);
            });
        }
    }
    /**
     * Initialize all UI interactions
     */
    initialize() {
        this._initializePanelStates();        // Set initial panel states (Ensure Hidden)
        this._setupProjectsPanelToggle();        // Setup projects panel toggle buttons
        this._setupFiltersPanelToggle();        // Setup filters panel toggle buttons
        this._setupCloseButtons();        // Setup close buttons
        this._setupMapControlButtons();        // Setup map control buttons
        this._setupSectionScrollButtons();        // Setup section scroll buttons
        this._setupProjectItemClickHandlers();        // Setup project item click handlers
        this._setupPanelBackgroundClickHandler();        // Setup panel background click handler
        this._updateSectionWrapperVisibility();        // Hide empty section wrappers on initial load
        this.isPanelInitialized = true;
    }
    // ==========================================
    // PANEL INITIALIZATION
    // ==========================================
    /**
     * Set initial state: both panels hidden
     * @private
     */
    _initializePanelStates() {
        const projectsPanel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        const filtersPanel = document.getElementById(UIManager.CONFIG.FILTERS_PANEL_ID);
        if (projectsPanel) {
            projectsPanel.style.display = 'none';
        }
        if (filtersPanel) {
            filtersPanel.style.display = 'none';
        }
    }
    // ==========================================
    // PROJECTS PANEL TOGGLE
    // ==========================================
    /**
     * Setup projects panel toggle button
     * @private
     */
    _setupProjectsPanelToggle() {
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (!toggleBtn) {
            return;
        }
        toggleBtn.addEventListener('click', () => {
            const panel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
            const isVisible = panel && panel.style.display !== 'none';
            if (isVisible) {
                this.closeProjectsPanel();
            } else {
                this.openProjectsPanel();
            }
        });
    }
    /**
     * Open projects panel
     * If there's an active marker, recenter the map to account for panel padding
     */
    openProjectsPanel() {
        const panel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (panel) {
            // On mobile, close filters panel if it's open (mutually exclusive)
            if (this.isMobile) {
                const filtersPanel = document.getElementById(UIManager.CONFIG.FILTERS_PANEL_ID);
                if (filtersPanel && filtersPanel.style.display !== 'none') {
                    this.closeFiltersPanel();
                }
            }
            panel.style.display = 'flex'; // Use flex to maintain layout
            if (toggleBtn) toggleBtn.classList.add(UIManager.CONFIG.ACTIVE_CLASS);
            // Recenter map if there's an active marker
            this.recenterMapToActiveMarker({ withPanelPadding: true });
        }
    }
    /**
     * Close projects panel (does not reset active project state)
     * If there's an active marker, recenter the map to account for removed padding
     */
    closeProjectsPanel() {
        const panel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (panel) {
            // Deactivate active project before closing panel
            if (this.activeProjectSlug) {
                this.deactivateProject(this.activeProjectSlug);
            }
            panel.style.display = 'none';
            if (toggleBtn) toggleBtn.classList.remove(UIManager.CONFIG.ACTIVE_CLASS);
            // Recenter map if there was an active marker (without padding)
            this.recenterMapToActiveMarker({ withPanelPadding: false });
        }
    }
    // ==========================================
    // FILTERS PANEL TOGGLE
    // ==========================================
    /**
     * Setup filters panel toggle buttons (desktop and mobile)
     * @private
     */
    _setupFiltersPanelToggle() {
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        // Handler function for both buttons
        const handleToggle = () => {
            const panel = document.getElementById(UIManager.CONFIG.FILTERS_PANEL_ID);
            const isVisible = panel && panel.style.display !== 'none';
            if (isVisible) {
                this.closeFiltersPanel();
            } else {
                this.openFiltersPanel();
            }
        };
        // Attach to desktop toggle button
        if (toggleBtn) {
            toggleBtn.addEventListener('click', handleToggle);
        } else {
            console.warn(`⚠️ Filters panel toggle button not found (ID: ${UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID})`);
        }
        // Attach to mobile toggle button
        if (toggleBtnAlt) {
            toggleBtnAlt.addEventListener('click', handleToggle);
        }
    }
    /**
     * Open filters panel
     * Panel opens at its original height (defined by CSS/Webflow)
     */
    openFiltersPanel() {
        const panel = document.getElementById(UIManager.CONFIG.FILTERS_PANEL_ID);
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        if (panel) {
            // On mobile, close projects panel if it's open (mutually exclusive)
            if (this.isMobile) {
                const projectsPanel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
                if (projectsPanel && projectsPanel.style.display !== 'none') {
                    this.closeProjectsPanel();
                }
            }
            panel.style.display = 'flex'; // Use flex to maintain layout
            // Reset position and opacity in case it was dragged
            if (typeof gsap !== 'undefined') {
                gsap.set(panel, { y: 0, opacity: 1 });
            }
            if (toggleBtn) toggleBtn.classList.add(UIManager.CONFIG.ACTIVE_CLASS);
            if (toggleBtnAlt) toggleBtnAlt.classList.add(UIManager.CONFIG.ACTIVE_CLASS);
        }
    }
    /**
     * Close filters panel
     */
    closeFiltersPanel() {
        const panel = document.getElementById(UIManager.CONFIG.FILTERS_PANEL_ID);
        const toggleBtn = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById(UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        if (panel) {
            panel.style.display = 'none';
            if (toggleBtn) toggleBtn.classList.remove(UIManager.CONFIG.ACTIVE_CLASS);
            if (toggleBtnAlt) toggleBtnAlt.classList.remove(UIManager.CONFIG.ACTIVE_CLASS);
        }
    }
    // ==========================================
    // CLOSE BUTTONS
    // ==========================================
    /**
     * Setup all close buttons
     * @private
     */
    _setupCloseButtons() {
        // Close projects panel button
        const closeProjectsBtn = document.getElementById(UIManager.CONFIG.CLOSE_PROJECTS_BTN_ID);
        if (closeProjectsBtn) {
            closeProjectsBtn.addEventListener('click', () => {
                this.closeProjectsPanel();
            });
        }
        // Close filters panel button
        const closeFiltersBtn = document.getElementById(UIManager.CONFIG.CLOSE_FILTERS_BTN_ID);
        if (closeFiltersBtn) {
            closeFiltersBtn.addEventListener('click', () => {
                this.closeFiltersPanel();
            });
        }
    }
    // ==========================================
    // PROJECT ITEM VISIBILITY (FILTERING)
    // ==========================================
    /**
     * Update project item visibility based on filtered locations
     * Called by FilterManager when filters change
     * @param {Array} filteredLocations - Array of filtered location features
     * @public
     */
    updateProjectItemsVisibility(filteredLocations) {
        // Get all visible project slugs from filtered locations
        const visibleSlugs = new Set(
            filteredLocations.map(location => location.properties.id)
        );
        // Get all project items
        const projectItems = document.querySelectorAll('.projects__item');
        let hiddenCount = 0;
        let visibleCount = 0;
        projectItems.forEach(projectItem => {
            // Find the .project element inside the .projects__item
            const projectElement = projectItem.querySelector('.project');
            if (!projectElement) return;
            const projectSlug = projectElement.id;
            if (visibleSlugs.has(projectSlug)) {
                // Show the project item
                projectItem.style.display = '';
                visibleCount++;
            } else {
                // Hide the project item
                projectItem.style.display = 'none';
                hiddenCount++;
                // If this project was active, deactivate it
                if (this.activeProjectSlug === projectSlug) {
                    this._deactivateProjectElement(projectElement);
                    this.activeProjectSlug = null;
                    // Also deactivate marker
                    if (this.markerManager) {
                        this.markerManager.deactivateAllMarkers(true);
                    }
                }
            }
        });
        // Hide section wrappers that have no visible project items
        this._updateSectionWrapperVisibility();
    }
    /**
     * Hide/show section wrappers based on whether they contain visible project items
     * Adds 'is--hidden' class to section wrappers with no visible projects
     * @private
     */
    _updateSectionWrapperVisibility() {
        // Get all section wrappers
        const sectionWrappers = document.querySelectorAll('.section__wrapper');
        let hiddenSections = 0;
        let visibleSections = 0;
        sectionWrappers.forEach(sectionWrapper => {
            // Find all project items within this section wrapper
            const projectItems = sectionWrapper.querySelectorAll('.projects__item');
            // Count how many are visible
            const visibleProjectsCount = Array.from(projectItems).filter(item => {
                return item.style.display !== 'none';
            }).length;
            // If no visible projects, hide the section wrapper
            if (visibleProjectsCount === 0) {
                sectionWrapper.classList.add('is--hidden');
                hiddenSections++;
            } else {
                sectionWrapper.classList.remove('is--hidden');
                visibleSections++;
            }
        });
    }
    // ==========================================
    // PROJECT ITEM CLICK HANDLERS
    // ==========================================
    /**
     * Setup click handler for projects panel background
     * Deactivates projects when clicking directly on the panel (not children)
     * @private
     */
    _setupPanelBackgroundClickHandler() {
        const projectsPanel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) {
            return;
        }
        projectsPanel.addEventListener('click', (e) => {
            // Only deactivate if clicking directly on the panel, not its children
            if (e.target === projectsPanel) {
                this._deactivateAllProjects();
                this.activeProjectSlug = null;
                // Deactivate corresponding marker
                if (this.markerManager && this.markerManager.deactivateAllMarkers) {
                    this.markerManager.deactivateAllMarkers(true);
                }
            }
        });
    }
    /**
     * Setup click handlers for project items in the list
     * Uses event delegation on the content wrapper
     * @private
     */
    _setupProjectItemClickHandlers() {
        const contentWrapper = document.querySelector(`.${UIManager.CONFIG.CONTENT_WRAPPER_CLASS}`);
        if (!contentWrapper) {
            return;
        }
        // Use event delegation for better performance
        // Listen for clicks on the content wrapper container
        contentWrapper.addEventListener('click', (e) => {
            // Find the closest project item element (.project class)
            const projectItem = e.target.closest('.project');
            if (!projectItem) return;
            // Get project slug from element ID
            const projectSlug = projectItem.id;
            if (projectSlug) {
                this.activateProject(projectSlug);
            }
        });
    }
    // ==========================================
    // PROJECT ACTIVATION
    // ==========================================
    /**
     * Activate a project item by expanding it inline
     * @param {string} projectSlug - The slug of the project to activate
     * @public
     */
    activateProject(projectSlug, options = {}) {
        if (!projectSlug) {
            return;
        }
        console.log(`UI-m: Activating project: ${projectSlug}`);
        // Find the project element by ID
        const projectElement = document.getElementById(projectSlug);
        if (!projectElement) {
            console.warn(`UI-m: ⚠️ Project element not found: ${projectSlug}`);
            return;
        }
        // Check if this project is already active
        const projectsItem = projectElement.closest('.projects__item');
        const isAlreadyActive = projectsItem?.classList.contains(UIManager.CONFIG.ACTIVE_CLASS);
        if (isAlreadyActive) {
            this.deactivateProject(projectSlug); // Deactivate if clicking the same project
            return;
        }
        this._deactivateAllProjects(); // Deactivate all other projects first
        this._activateProjectElement(projectElement); // Activate this project
        this._scrollToProject(projectElement); // Scroll to the project in the panel
        this.activeProjectSlug = projectSlug; // Store active project slug
        if (window.animationManager) { // Snap to active project height on mobile
            window.animationManager.snapToActiveProjectHeight();
        }
        if (this.markerManager && this.markerManager.activateMarkerBySlug) { // Activate corresponding marker on map
            this.markerManager.activateMarkerBySlug(projectSlug, options);
        }
        console.log(`UI-m: Project activated: ${projectSlug}`);
    }
    /**
     * Deactivate a specific project
     * @param {string} projectSlug - The slug of the project to deactivate
     * @public
     */
    deactivateProject(projectSlug) {
        const projectElement = document.getElementById(projectSlug);
        if (!projectElement) {
            return;
        }
        this._deactivateProjectElement(projectElement);
        this.activeProjectSlug = null;
        // Deactivate corresponding marker (with skipUISync to prevent circular call)
        if (this.markerManager && this.markerManager.deactivateAllMarkers) {
            this.markerManager.deactivateAllMarkers(true);
        }
    }
    /**
     * Activate a project element (add classes, show metadata)
     * @private
     */
    _activateProjectElement(projectElement) {
        // Find parent .projects__item
        const projectsItem = projectElement.closest('.projects__item');
        if (!projectsItem) {
            return;
        }
        // Add is--active class to .projects__item
        projectsItem.classList.add(UIManager.CONFIG.ACTIVE_CLASS);
        // Force column and row span to 2 (grid-column: span 2; grid-row: span 2;)
        projectsItem.style.gridColumn = 'span 2';
        projectsItem.style.gridRow = 'span 2';
        // Find and activate .project-image__wrapper
        const imageWrapper = projectElement.querySelector('.project-image__wrapper');
        if (imageWrapper) {
            imageWrapper.classList.add(UIManager.CONFIG.ACTIVE_CLASS);
        }
        // Find and scale up h3.project__name (1.25x)
        const projectName = projectElement.querySelector('h3.project__name');
        if (projectName) {
            projectName.style.fontSize = '1.25em';
        }
        // Hide plus button, show minus button
        const plusButton = projectElement.querySelector('[project-item-button="plus"]');
        if (plusButton) {
            plusButton.classList.add(UIManager.CONFIG.HIDDEN_CLASS);
        }
        const minusButton = projectElement.querySelector('[project-item-button="minus"]');
        if (minusButton) {
            minusButton.classList.remove(UIManager.CONFIG.HIDDEN_CLASS);
        }
        // Show metadata container
        const metadataContainer = projectElement.querySelector('.metadata__container.is--hidden');
        if (metadataContainer) {
            metadataContainer.classList.remove('is--hidden');
        }
    }
    /**
     * Deactivate a project element (remove classes, hide metadata)
     * @private
     */
    _deactivateProjectElement(projectElement) {
        // Find parent .projects__item
        const projectsItem = projectElement.closest('.projects__item');
        if (!projectsItem) return;
        // Remove is--active class from .projects__item
        projectsItem.classList.remove(UIManager.CONFIG.ACTIVE_CLASS);
        // Reset grid span
        projectsItem.style.gridColumn = '';
        projectsItem.style.gridRow = '';
        // Deactivate .project-image__wrapper
        const imageWrapper = projectElement.querySelector('.project-image__wrapper');
        if (imageWrapper) {
            imageWrapper.classList.remove(UIManager.CONFIG.ACTIVE_CLASS);
        }
        // Reset h3.project__name font size
        const projectName = projectElement.querySelector('h3.project__name');
        if (projectName) {
            projectName.style.fontSize = '';
        }
        // Show plus button, hide minus button
        const plusButton = projectElement.querySelector('[project-item-button="plus"]');
        if (plusButton) {
            plusButton.classList.remove(UIManager.CONFIG.HIDDEN_CLASS);
        }
        const minusButton = projectElement.querySelector('[project-item-button="minus"]');
        if (minusButton) {
            minusButton.classList.add(UIManager.CONFIG.HIDDEN_CLASS);
        }
        // Hide metadata container
        const metadataContainer = projectElement.querySelector('.metadata__container');
        if (metadataContainer && !metadataContainer.classList.contains('is--hidden')) {
            metadataContainer.classList.add('is--hidden');
        }
    }
    /**
     * Deactivate all projects
     * @private
     */
    _deactivateAllProjects() {
        const contentWrapper = document.querySelector(`.${UIManager.CONFIG.CONTENT_WRAPPER_CLASS}`);
        if (!contentWrapper) return;
        const allProjectElements = contentWrapper.querySelectorAll('.project');
        allProjectElements.forEach(projectElement => {
            this._deactivateProjectElement(projectElement);
        });
    }
    /**
     * Scroll to a project element in the panel
     * @private
     */
    _scrollToProject(projectElement) {
        if (!projectElement) return;
        // Find the scrollable container (projects panel content area)
        const projectsPanel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) {
            return;
        }
        // Get the scrollable container (might be the panel itself or a child)
        const scrollContainer = projectsPanel.querySelector('.content-wrapper') || projectsPanel;
        // Calculate the position to scroll to
        const projectsItem = projectElement.closest('.projects__item');
        if (projectsItem && scrollContainer) {
            // Scroll to element with offset
            const elementTop = projectsItem.offsetTop;
            const offset = 32;
            scrollContainer.scrollTo({
                top: elementTop - offset,
                behavior: 'instant'
            });
        }
    }
    // ==========================================
    // MAP CONTROL BUTTONS
    // ==========================================
    /**
     * Setup map control buttons (zoom, orientation, satellite)
     * @private
     */
    _setupMapControlButtons() {
        // Zoom In button
        const zoomInBtn = document.getElementById(UIManager.CONFIG.MAP_ZOOM_IN_BTN_ID);
        if (zoomInBtn) {
            zoomInBtn.addEventListener('click', () => {
                this.mapManager.zoomIn();
            });
        }
        // Zoom Out button
        const zoomOutBtn = document.getElementById(UIManager.CONFIG.MAP_ZOOM_OUT_BTN_ID);
        if (zoomOutBtn) {
            zoomOutBtn.addEventListener('click', () => {
                this.mapManager.zoomOut();
            });
        }
        // Orientation Reset button
        const orientationBtn = document.getElementById(UIManager.CONFIG.MAP_ORIENTATION_BTN_ID);
        if (orientationBtn) {
            orientationBtn.addEventListener('click', () => {
                this.mapManager.resetOrientation();
            });
        }
        // Reset Map View / Fly Out button
        const resetViewBtn = document.getElementById(UIManager.CONFIG.RESET_MAP_VIEW_BTN_ID);
        if (resetViewBtn) {
            resetViewBtn.addEventListener('click', () => {
                this.closeFiltersPanel(); // Close filters panel
                this.closeProjectsPanel(); // Close projects panel
                if (this.markerManager) { // Deactivate all markers
                    this.markerManager.deactivateAllMarkers();
                }
                this.mapManager.flyOut(); // Fly out with smart zoom logic
            });
        }
    }
    // ==========================================
    // SECTION SCROLL BUTTONS
    // ==========================================
    /**
     * Setup section scroll buttons (scroll to next/previous section)
     * @private
     */
    _setupSectionScrollButtons() {
        const scrollDownBtn = document.getElementById(UIManager.CONFIG.SCROLL_DOWN_BTN_ID);
        const scrollUpBtn = document.getElementById(UIManager.CONFIG.SCROLL_UP_BTN_ID);
        if (scrollDownBtn) {
            scrollDownBtn.addEventListener('click', () => {
                this._scrollToNextSection('down');
            });
        }
        if (scrollUpBtn) {
            scrollUpBtn.addEventListener('click', () => {
                this._scrollToNextSection('up');
            });
        }
    }
    /**
     * Scroll to the next section (up or down)
     * @param {string} direction - 'up' or 'down'
     * @private
     */
    _scrollToNextSection(direction) {
        const projectsPanel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) return;
        // Get the scrollable container (content-wrapper or panel itself)
        const scrollContainer = projectsPanel.querySelector('.content-wrapper') || projectsPanel;
        // Get all section headers in the projects panel that are visible
        const sectionHeaders = Array.from(scrollContainer.querySelectorAll('.section-header__wrapper'))
            .filter(header => {
                // Only include visible section headers (not hidden by filters)
                const sectionWrapper = header.closest('.section__wrapper');
                return !sectionWrapper || !sectionWrapper.classList.contains('is--hidden');
            });
        if (sectionHeaders.length === 0) return;
        // Get current scroll position and container dimensions
        const scrollTop = scrollContainer.scrollTop;
        const containerHeight = scrollContainer.clientHeight;
        const scrollHeight = scrollContainer.scrollHeight;
        const viewportTop = scrollTop;
        const viewportBottom = scrollTop + containerHeight;
        let targetSection = null;
        if (direction === 'down') {
            // Check if we're at or near the bottom
            const isAtBottom = (scrollTop + containerHeight >= scrollHeight - 50); // 50px threshold
            if (isAtBottom) {
                // We're at the bottom, wrap to first section
                targetSection = sectionHeaders[0];
            } else {
                // Find the first section that is NOT currently fully in view
                for (let i = 0; i < sectionHeaders.length; i++) {
                    const sectionTop = sectionHeaders[i].offsetTop;
                    // If section starts below current viewport top, it's the next one
                    if (sectionTop > viewportTop + 30) { // 30px margin to avoid current section
                        targetSection = sectionHeaders[i];
                        break;
                    }
                }
                // If no section found below, wrap to first section
                if (!targetSection && sectionHeaders.length > 0) {
                    targetSection = sectionHeaders[0];
                }
            }
        } else if (direction === 'up') {
            // Find the previous section (not currently at top of viewport)
            for (let i = sectionHeaders.length - 1; i >= 0; i--) {
                const sectionTop = sectionHeaders[i].offsetTop;
                // If section starts above current viewport top (minus margin), it's the previous one
                if (sectionTop < viewportTop - 30) { // 30px margin
                    targetSection = sectionHeaders[i];
                    break;
                }
            }
            // If no section found above, wrap to last section
            if (!targetSection && sectionHeaders.length > 0) {
                targetSection = sectionHeaders[sectionHeaders.length - 1];
            }
        }
        // Scroll to target section
        if (targetSection) {
            // Check if mobile for different offset
            const offset = this.isMobile ? 22 : 5; // 20px on mobile, 5px on desktop
            scrollContainer.scrollTo({
                top: targetSection.offsetTop - offset,
                behavior: 'smooth'
            });
        }
    }
    /**
     * Recenter map to active marker with appropriate padding
     * Centralized method to handle all recentering logic
     * @param {Object} options - Options for recentering
     * @param {boolean} options.withPanelPadding - Whether to include panel padding (default: current panel state)
     * @param {number} options.duration - Animation duration in ms (default: 750)
     * @returns {boolean} Whether recentering was performed
     */
    recenterMapToActiveMarker(options = {}) {
        const {
            withPanelPadding = null, // null = auto-detect from panel state
            duration = 750
        } = options;
        // Check if there's an active marker
        const hasActiveMarker = this.markerManager && this.markerManager.activeMarkerSlug;
        if (!hasActiveMarker) {
            return false; // No active marker, nothing to recenter
        }
        // Get the active marker's coordinates
        const activeMarker = this.markerManager.markers.find(marker => {
            const element = marker.getElement();
            return element && element.dataset.slug === this.markerManager.activeMarkerSlug;
        });
        if (!activeMarker) {
            return false; // Marker not found
        }
        const lngLat = activeMarker.getLngLat();
        const activeMarkerCoordinates = [lngLat.lng, lngLat.lat];
        // Get the map instance
        const map = this.mapManager.getMap();
        if (!map) {
            return false; // Map not available
        }
        // Determine padding and ensure all values are numbers
        let padding;
        if (withPanelPadding === null) {
            // Auto-detect: use current panel state
            padding = this.getMapPadding();
        } else if (withPanelPadding === true) {
            // Force use panel padding
            padding = this.getMapPadding();
        } else {
            // No padding
            padding = { top: 0, bottom: 0, left: 0, right: 0 };
        }
        // Double-check all padding values are numbers (Mapbox requirement)
        padding = {
            top: Number(padding.top) || 0,
            bottom: Number(padding.bottom) || 0,
            left: Number(padding.left) || 0,
            right: Number(padding.right) || 0
        };
        // Smoothly recenter the map
        map.easeTo({
            center: activeMarkerCoordinates,
            duration: duration,
            padding: padding,
            easing: (t) => t * (2 - t) // easeOutQuad
        });
        return true;
    }
    /**
     * Get map padding to account for open panels
     * Used by markerManager when flying to markers
     * @returns {Object} Padding object { top, bottom, left, right } - all numbers
     */
    getMapPadding() {
        const panel = document.getElementById(UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!panel) {
            return { top: 0, bottom: 0, left: 0, right: 0 };
        }
        // Check if panel is actually visible (using display style, not class)
        const panelHidden = panel.style.display === 'none';
        if (this.isMobile && !panelHidden) {
            // Mobile: Panel is at bottom - add bottom padding (panel height + 2rem offset)
            const panelHeight = panel.offsetHeight || 0;
            const emUnit = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const offset = 1 * emUnit; // 1rem above panel
            const bottomPadding = Math.max(0, panelHeight + offset);
            return {
                top: 0,
                bottom: bottomPadding,
                left: 0,
                right: 0
            };
        } else if (!this.isMobile && !panelHidden) {
            // Desktop: Panel is on left side - add left padding
            const panelWidth = Number(UIManager.CONFIG.PROJECTS_PANEL_WIDTH) || 0;
            return {
                top: 0,
                bottom: 0,
                left: panelWidth,
                right: 0
            };
        }
        // Panel is closed - no padding needed
        return { top: 0, bottom: 0, left: 0, right: 0 };
    }
}
// ! EXPORT
export default UIManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.UIManager = UIManager;
}
